import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';

export type TownProvisionTemplate = 'supply_handcart' | 'net_drying_rack' | 'provision_barrels' | 'rope_well';

function wheel(x: number, p: TownPropPalette): string {
  return `<g data-part="cart-wheel">
    <circle cx="${x}" cy="88" r="16" fill="${hex(p.iron)}" />
    <path d="M${x - 5} 102H${x + 5}L${x + 4} 104H${x - 4}Z" fill="${hex(p.iron)}" />
    <circle cx="${x}" cy="88" r="12.6" fill="${hex(p.woodEdge)}" />
    <circle cx="${x}" cy="88" r="10" fill="${hex(p.shade)}" />
    <path d="M${x - 10} 88H${x + 10} M${x} 78V98 M${x - 7} 81L${x + 7} 95 M${x - 7} 95L${x + 7} 81"
      stroke="${hex(p.woodEdge)}" stroke-width="1.7" />
    <circle cx="${x}" cy="88" r="4" fill="${hex(p.iron)}" />
    <circle cx="${x - 0.7}" cy="87.3" r="1.5" fill="${hex(p.stone)}" />
  </g>`;
}

/** 156×104. Wheels carry the bed; the long handles attach to its underside. */
function handcart(p: TownPropPalette): string {
  const wood = hex(p.wood), edge = hex(p.woodEdge), iron = hex(p.iron);
  return `<g data-part="cart-running-gear">
    <path d="M37 71L48 89H114L124 73" fill="none" stroke="${wood}" stroke-width="7" stroke-linejoin="round" />
    <path d="M107 68L153 78V83L105 75Z M111 77L151 90V94L109 83Z" fill="${wood}" />
    <path d="M123 74L151 80 M125 85L149 92" stroke="${edge}" stroke-width="1.3" />
    <path d="M145 77L154 79V83L145 81Z M144 88L153 91V95L144 92Z" fill="${iron}" />
  </g>
  <g data-part="cart-bed">
    <path d="M24 56L118 51L135 68L123 79H24L18 70Z" fill="${wood}" />
    <path d="M24 56L118 51L135 68H31Z" fill="${edge}" />
    <path d="M24 70H132V78H24Z" fill="${wood}" />
    <path d="M25 71H127 M31 75H123" stroke="${edge}" stroke-width="1.2" />
    <path d="M24 51H30V77H24Z M119 48H125V74H119Z" fill="${wood}" />
    <path d="M25 55H29 M120 52H124" stroke="${iron}" stroke-width="3" />
  </g>
  <g data-part="covered-crate">
    <path d="M39 34L98 30L117 42V67H40Z" fill="${wood}" />
    <path d="M98 31L117 42V66L99 62Z" fill="${hex(p.shade)}" />
    <path d="M43 42V64 M62 39V65 M81 38V65 M101 41V61 M111 46V63 M41 54H98 M44 62L89 38"
      fill="none" stroke="${edge}" stroke-width="1.5" />
    <path d="M35 36Q45 17 65 17L96 19Q111 23 119 39L112 56L93 53L73 57L54 51L39 56Z" fill="${hex(p.primary)}" />
    <path d="M96 19Q108 35 101 54L112 56L119 39Q111 23 96 19Z" fill="${hex(p.shade)}" />
    <path d="M37 37Q66 30 103 37 M58 21Q53 35 57 49 M77 22Q79 37 73 53 M108 33L106 48"
      fill="none" stroke="${hex(p.stone)}" stroke-width="1.1" opacity="0.65" />
    <path d="M49 29L48 64 M89 26L92 64 M46 61L95 62" fill="none" stroke="${edge}" stroke-width="2" />
    <path d="M46 44L51 49 M46 49L51 44 M90 45L95 50 M90 50L95 45" stroke="${iron}" stroke-width="1.3" />
  </g>
  <g data-part="cart-front-sack">
    <path d="M19 68Q15 57 22 49L26 47L25 41L34 41L32 48Q44 55 39 69Z" fill="${hex(p.stone)}" />
    <path d="M24 46L34 46 M25 50Q21 57 24 65 M31 51L34 63" fill="none" stroke="${wood}" stroke-width="1.2" />
  </g>
  ${wheel(48, p)}${wheel(112, p)}`;
}

function hangingFish(x: number, y: number, scale: number, p: TownPropPalette): string {
  return `<g data-part="drying-fish" transform="translate(${x} ${y}) scale(${scale})">
    <path d="M0 -8V2" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    <path d="M0 7L-7 0H7Z M0 5Q10 13 8 26Q4 34 0 36Q-7 31 -8 24Q-9 13 0 5Z" fill="${hex(p.stone)}" />
    <path d="M0 8Q5 18 3 30 M-5 16L-10 20L-6 23 M6 22L11 26L6 28" fill="none" stroke="${hex(p.glassCore)}" stroke-width="1.3" />
    <path d="M-5 12L4 16 M-6 19L4 23 M-4 26L3 29" stroke="${hex(p.woodEdge)}" stroke-width="0.85" />
    <circle cx="-2" cy="30" r="1.3" fill="${hex(p.iron)}" />
  </g>`;
}

/** 152×136. Individual diamonds follow the net's sag without an overflow clip. */
function netRack(p: TownPropPalette): string {
  const wood = hex(p.wood), edge = hex(p.woodEdge), rope = hex(p.stone);
  const node = (column: number, row: number) => ({ x: 30 + column * 11.5,
    y: 27 + row * 11 + Math.sin(column / 8 * Math.PI) * row * 1.55 });
  let mesh = '';
  for (let row = 0; row < 6; row++) for (let column = 0; column < 8; column++) {
    const a = node(column, row), b = node(column + 1, row + 1);
    const c = node(column + 1, row), d = node(column, row + 1);
    mesh += `M${a.x} ${a.y.toFixed(2)}L${b.x} ${b.y.toFixed(2)} M${c.x} ${c.y.toFixed(2)}L${d.x} ${d.y.toFixed(2)} `;
  }
  return `<g data-part="rack-frame">
    <path d="M18 17H26V136H18Z M128 17H136V136H128Z" fill="${wood}" />
    <path d="M22 92L7 133 M132 92L146 133" stroke="${wood}" stroke-width="5" />
    <path d="M20 30V124 M130 30V124" stroke="${edge}" stroke-width="1.4" />
    <rect x="12" y="14" width="130" height="9" rx="1.5" fill="${wood}" />
    <path d="M14 16H140 M24 33L42 23 M113 23L130 33" stroke="${edge}" stroke-width="1.7" />
    <path d="M14 20H29 M124 20H139 M17 24L28 24 M126 24H138" stroke="${rope}" stroke-width="1.6" />
    <path d="M5 132H30V136H5Z M120 132H150V136H120Z" fill="${hex(p.iron)}" />
  </g>
  <g data-part="drying-net">
    <path d="M30 27H122V93Q76 112 30 93Z" fill="${hex(p.shade)}" opacity="0.3" />
    <path d="${mesh}" fill="none" stroke="${rope}" stroke-width="0.85" opacity="0.75" />
    <path d="M30 23V93Q76 112 122 93V23 M31 28Q76 34 121 28" fill="none" stroke="${edge}" stroke-width="1.7" />
    ${[30, 53, 76, 99, 122].map(x => `<path d="M${x - 2} 19Q${x - 4} 25 ${x} 28Q${x + 4} 25 ${x + 2} 19" fill="none" stroke="${rope}" stroke-width="1.1" />`).join('')}
  </g>
  ${hangingFish(51, 41, 1, p)}${hangingFish(84, 48, 0.85, p)}${hangingFish(105, 36, 0.9, p)}
  <g data-part="net-buoys">
    <path d="M33 61V82 M117 73V94" stroke="${rope}" stroke-width="1.1" />
    <ellipse cx="33" cy="82" rx="5" ry="8" fill="${hex(p.primary)}" />
    <ellipse cx="117" cy="94" rx="5" ry="7" fill="${hex(p.glass)}" />
    <path d="M29 80H37 M113 93H121" stroke="${hex(p.glassCore)}" stroke-width="2.5" />
    <path d="M31 88L35 88 M115 99H119" stroke="${hex(p.iron)}" stroke-width="1.5" />
  </g>`;
}

function barrel(x: number, y: number, width: number, height: number, p: TownPropPalette): string {
  const w = width, h = height;
  return `<g data-part="provision-barrel" transform="translate(${x} ${y})">
    <path d="M4 6Q-1 ${h / 2} 4 ${h - 5}Q${w / 2} ${h + 5} ${w - 4} ${h - 5}Q${w + 1} ${h / 2} ${w - 4} 6Z" fill="${hex(p.wood)}" />
    <ellipse cx="${w / 2}" cy="${h - 4}" rx="${w / 2 - 4}" ry="4" fill="${hex(p.iron)}" />
    <path d="M${w * 0.6} 6Q${w * 0.72} ${h / 2} ${w * 0.6} ${h - 5}L${w - 4} ${h - 5}Q${w + 1} ${h / 2} ${w - 4} 6Z" fill="${hex(p.shade)}" />
    <path d="M9 9Q5 ${h / 2} 9 ${h - 7} M${w * 0.36} 10Q${w * 0.3} ${h / 2} ${w * 0.36} ${h - 5}
      M${w * 0.61} 10V${h - 6} M${w - 10} 9Q${w - 6} ${h / 2} ${w - 10} ${h - 7}" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    <path d="M3 ${h * 0.27}Q${w / 2} ${h * 0.27 + 7} ${w - 3} ${h * 0.27}
      M3 ${h * 0.76}Q${w / 2} ${h * 0.76 + 7} ${w - 3} ${h * 0.76}" fill="none" stroke="${hex(p.iron)}" stroke-width="3.8" />
    <path d="M4 ${h * 0.27 - 1}Q${w / 2} ${h * 0.27 + 5} ${w - 4} ${h * 0.27 - 1}" fill="none" stroke="${hex(p.stone)}" stroke-width="0.75" opacity="0.65" />
    <ellipse cx="${w / 2}" cy="6" rx="${w / 2 - 3}" ry="5.5" fill="${hex(p.woodEdge)}" />
    <ellipse cx="${w / 2}" cy="6" rx="${w / 2 - 6}" ry="3.5" fill="${hex(p.wood)}" />
    <path d="M${w * 0.32} 3V9 M${w * 0.54} 3V9 M${w * 0.74} 4V8" stroke="${hex(p.woodEdge)}" stroke-width="0.8" />
    <path d="M${w * 0.43} ${h * 0.39}L${w * 0.56} ${h * 0.38}V${h * 0.65}L${w * 0.43} ${h * 0.64}Z" fill="${hex(p.primary)}" opacity="0.8" />
    <circle cx="${w * 0.49}" cy="${h * 0.5}" r="1.3" fill="${hex(p.iron)}" />
  </g>`;
}

/** 112×88. The raised cask sits behind two unequal grounded casks. */
function barrels(p: TownPropPalette): string {
  return `${barrel(42, 2, 38, 47, p)}${barrel(7, 34, 45, 54, p)}${barrel(59, 38, 44, 50, p)}
    <g data-part="barrel-chocks" fill="${hex(p.woodEdge)}">
      <path d="M3 88L7 83H14V88Z M96 88V83H102L109 88Z" />
    </g>`;
}

/** 128×168. Open masonry pit, timber windlass and a bucket beneath the canopy. */
function well(p: TownPropPalette): string {
  const wood = hex(p.wood), edge = hex(p.woodEdge), stone = hex(p.stone), iron = hex(p.iron);
  return `<g data-part="well-timber-frame">
    <path d="M26 42H34V153H26Z M94 42H102V153H94Z" fill="${wood}" />
    <path d="M28 48V138 M96 48V138 M34 52L49 67 M79 67L94 52" stroke="${edge}" stroke-width="1.7" />
    <path d="M26 72H103V80H26Z" fill="${wood}" />
    <path d="M28 73H100" stroke="${edge}" stroke-width="1.5" />
    <ellipse cx="64" cy="76" rx="11" ry="8" fill="${edge}" />
    <path d="M57 71V81 M61 69V83 M65 69V83 M69 70V82" stroke="${wood}" stroke-width="1.2" />
    <path d="M102 76H112V91H119" fill="none" stroke="${iron}" stroke-width="3" stroke-linejoin="round" />
    <path d="M116 87H122V96H116Z" fill="${wood}" />
  </g>
  <g data-part="well-canopy">
    <path d="M6 44L55 5Q64 1 73 5L122 44L113 48H15Z" fill="${hex(p.primary)}" />
    <path d="M64 4L122 44L113 48H64Z" fill="${hex(p.shade)}" />
    <path d="M48 13H79 M33 25H94 M19 37H108 M57 13L54 24 M72 13L76 24
      M43 25L37 36 M62 25V36 M83 25L90 36 M29 37L24 43 M50 37L48 44 M73 37L76 44 M97 37L102 43"
      stroke="${wood}" stroke-width="1.3" fill="none" />
    <path d="M9 43L56 7Q64 3 72 7L119 43 M15 46H113" stroke="${edge}" stroke-width="2.2" fill="none" stroke-linejoin="round" />
    <path d="M13 48H115V52H13Z" fill="${wood}" />
  </g>
  <g data-part="well-masonry">
    <path d="M17 133H111V154Q111 168 64 168Q17 168 17 154Z" fill="${stone}" />
    <path d="M20 143Q64 161 108 143 M20 155Q64 173 108 155" stroke="${iron}" stroke-width="1.2" fill="none" />
    <path d="M28 139V149 M50 147V158 M79 147V158 M99 139V150 M37 155V162 M91 155V162"
      stroke="${iron}" stroke-width="1.1" />
    <ellipse cx="64" cy="133" rx="47" ry="14" fill="${stone}" />
    <ellipse data-part="well-open-pit" cx="64" cy="132" rx="36" ry="9" fill="${iron}" />
    <path d="M22 132Q64 113 106 132 M26 135L34 132 M47 121L50 126 M80 122L77 127 M104 136L94 132"
      fill="none" stroke="${edge}" stroke-width="1.4" />
    <path d="M43 135Q63 131 84 135" stroke="${hex(p.glass)}" stroke-width="1.5" opacity="0.48" fill="none" />
  </g>
  <g data-part="well-rope-bucket">
    <path d="M64 83V119" stroke="${edge}" stroke-width="2.2" />
    <path d="M62 89L66 92 M62 99L66 102 M62 109L66 112" stroke="${wood}" stroke-width="0.8" />
    <path d="M56 123V118Q64 108 72 118V123" fill="none" stroke="${iron}" stroke-width="1.8" />
    <path d="M53 122H75L72 140H57Z" fill="${wood}" />
    <path d="M58 125L60 138 M64 125V138 M70 125L68 138" stroke="${edge}" stroke-width="1" />
    <path d="M54 124H74 M57 136H72" stroke="${iron}" stroke-width="2.2" />
    <path d="M57 122Q64 120 71 122" stroke="${hex(p.glassCore)}" stroke-width="0.8" fill="none" />
    <path d="M24 139Q64 151 104 139" stroke="${stone}" stroke-width="5" fill="none" />
  </g>`;
}

/** Pure, static bodies in catalog-owned frames; authored placement supplies bottom-center anchoring. */
export function buildTownProvisionBody(template: TownProvisionTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'supply_handcart': return handcart(palette);
  case 'net_drying_rack': return netRack(palette);
  case 'provision_barrels': return barrels(palette);
  case 'rope_well': return well(palette);
  }
}
